import type {ReactNode, RefObject} from 'react'
import {Hero} from '@primer/react-brand/esm'
import {usePersonalization} from '@github-ui/swp-core/contexts/PersonalizationContext'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import type {FlexSuiteBaseHeroProps} from './types'
import {getDescription, getHeading} from './utils'
import {resolveCtaWithPersonalisation} from './resolve-hero-ctas'

type HeroContentProps = FlexSuiteBaseHeroProps & {
  'data-testid'?: string
  children?: ReactNode
  className?: string
  imageContainerClassName?: string
  imageContainerRef?: RefObject<HTMLDivElement | null>
  headingClassName?: string
}

function getHeroCtaAnalyticsEvent(
  action: string,
  position: 'primary' | 'secondary',
  audienceCondition: string | undefined,
) {
  return getAnalyticsEvent({
    action,
    tag: 'button',
    context: `${position} CTA ${audienceCondition ?? 'default'}`,
    location: 'hero',
  })
}

export function HeroContent({
  heroHeading,
  heroLabel,
  heroDescription,
  heroCtaOne,
  heroCtaOnePersonalisation,
  heroCtaTwo,
  heroCtaTwoPersonalisation,
  heroTertiaryDescription,
  options,
  children,
  className,
  'data-testid': dataTestId,
  imageContainerClassName,
  imageContainerRef,
  headingClassName,
}: HeroContentProps) {
  const {userState} = usePersonalization()
  const {variant, enableAnimation, align, textVariant} = options
  const trailingComponent = heroTertiaryDescription
    ? () => getDescription(heroTertiaryDescription, textVariant)
    : undefined
  const {cta: resolvedHeroCtaOne, audienceCondition: heroCtaOneAudienceCondition} = resolveCtaWithPersonalisation(
    heroCtaOne,
    heroCtaOnePersonalisation,
    userState,
  )
  const {cta: resolvedHeroCtaTwo, audienceCondition: heroCtaTwoAudienceCondition} = resolveCtaWithPersonalisation(
    heroCtaTwo,
    heroCtaTwoPersonalisation,
    userState,
  )
  const hasPersonalizedCta = heroCtaOneAudienceCondition !== undefined || heroCtaTwoAudienceCondition !== undefined

  return (
    <>
      <meta name="octolytics-dimension-personalized" content={hasPersonalizedCta ? 'yes' : 'no'} />
      <Hero
        className={className}
        variant={variant}
        align={align}
        enableAnimation={enableAnimation}
        trailingComponent={trailingComponent}
        data-testid={dataTestId}
        imageContainerClassName={imageContainerClassName}
        imageContainerRef={imageContainerRef}
      >
        {heroLabel && <Hero.Label>{heroLabel}</Hero.Label>}
        <Hero.Heading className={headingClassName}>{getHeading(heroHeading)}</Hero.Heading>
        {heroDescription ? (
          <Hero.Description variant={textVariant}>{getDescription(heroDescription, textVariant)}</Hero.Description>
        ) : null}
        <Hero.PrimaryAction
          href={resolvedHeroCtaOne.fields.href}
          {...getHeroCtaAnalyticsEvent(resolvedHeroCtaOne.fields.text, 'primary', heroCtaOneAudienceCondition)}
        >
          {resolvedHeroCtaOne.fields.text}
        </Hero.PrimaryAction>
        {resolvedHeroCtaTwo && (
          <Hero.SecondaryAction
            href={resolvedHeroCtaTwo.fields.href}
            {...getHeroCtaAnalyticsEvent(resolvedHeroCtaTwo.fields.text, 'secondary', heroCtaTwoAudienceCondition)}
          >
            {resolvedHeroCtaTwo.fields.text}
          </Hero.SecondaryAction>
        )}
        {children}
      </Hero>
    </>
  )
}
